// src/views/Collaboration/Collaboration.style.js
import { styled } from "@mui/material/styles";
import {
  Box,
  Card,
  Avatar,
  Typography,
  Paper,
  TextField,
  Button,
} from "@mui/material";

const THEME = {
  primary: "rgb(44, 52, 123)",
  primaryRgba: (a) => `rgba(44, 52, 123, ${a})`,
  hover: "rgb(35, 42, 100)",
};

/** Layout - compact */
export const SIDEBAR_W = 250;
export const GUTTER_L = 8;
export const GUTTER_R = 8;
export const PAGE_Y = 8;

export const HEADER_H = 0;
export const APPBAR_H = 0;
export const CHAT_PADDING = 6;

/** Mandatory visual gap between popover and the right edge */
export const POPOVER_RIGHT_GAP_PX = 16;
export const POPOVER_WIDTH_PX = 340;

/** Page root */
export const RootBox = styled(Box)({
  display: "flex",
  width: "100%",
  height: "100%",
  backgroundColor: "#ffffff",
  flexDirection: "column",
});

export const UsersBox = styled(Box)({ display: "none" });

/** Header row - white topbar */
export const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  position: "sticky",
  top: 0,
  zIndex: 30,
  background: "#ffffff",
  borderBottom: "1px solid #e2e8f0",
});

export const UsersTitle = styled(Typography)({
  fontWeight: 600,
  letterSpacing: 0.2,
  color: "#1e293b",
  fontSize: "0.9rem",
});

/** Owner area - compact pill */
export const OwnerPill = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px 4px 6px",
  borderRadius: 12,
  background: "#f1f5f9",
  border: "1px solid #e2e8f0",
  minHeight: 28,
});

export const OwnerAvatar = styled(Avatar)({
  width: 24,
  height: 24,
  fontWeight: 600,
  fontSize: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
});

export const OwnerName = styled(Typography)({
  fontWeight: 600,
  color: "#334155",
  lineHeight: 1.1,
  fontSize: "0.75rem",
});

export const OwnerStatus = styled(Typography)({
  fontSize: 9,
  color: "#64748b",
  display: "flex",
  alignItems: "center",
  gap: 4,
  marginTop: 0,
});

/** Inline dot before Online/Offline */
export const StatusDotInline = styled("span")(({ online }) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: online ? "#22c55e" : "#9ca3af",
  display: "inline-block",
}));

/** Members preview spacer */
export const UsersList = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.25),
  padding: "2px 10px 10px 10px",
  // Make this section sticky too - position it below SQL section
  position: "sticky",
  top: "200px", // Adjust based on header + SQL height
  zIndex: 10,
  backgroundColor: "#ffffff",
}));

/** Query bar */
export const QueryBar = styled(Box)({
  background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
  color: "#E5E7EB",
  borderRadius: 0,
  margin: "0 0 10px 0",
  padding: "12px 16px",
  fontFamily:
    "'JetBrains Mono','Fira Code','Menlo','Monaco','Ubuntu Mono',monospace",
  fontSize: 12,
  lineHeight: 1.5,
  maxHeight: 140,
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  position: "sticky",
  top: "60px",
  zIndex: 20,
  borderLeft: "3px solid #64748b",
});

/** Content container */
export const ContentContainer = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  padding: 0,
  overflow: "hidden",
});

/** Chat shell - content area (no extra card, parent is the card) */
export const ChatArea = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: "#ffffff",
});

export const ChatShell = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  background: "transparent",
  position: "relative",
  overflow: "hidden",
  boxShadow: "none",
  height: "100%",
  minHeight: 0,
});

/** Messages area - chat view */
export const ChatCard = styled(Card)({
  flex: 1,
  minHeight: 0,
  borderRadius: 0,
  boxShadow: "none",
  padding: "8px",
  overflowY: "auto",
  overflowX: "hidden",
  "::-webkit-scrollbar": { width: 5 },
  "::-webkit-scrollbar-thumb": { background: THEME.primaryRgba(0.25), borderRadius: 12 },
  "::-webkit-scrollbar-track": { background: "transparent" },
  backgroundColor: "#ffffff",
});

/** Message row - avatar centered with message */
export const ChatMsgRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMine",
})(({ isMine }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: 5,
  gap: 5,
  justifyContent: isMine ? "flex-end" : "flex-start",
}));

export const ChatMsgAvatar = styled(Avatar)(() => ({
  flexShrink: 0,
  width: 32,
  height: 32,
  fontSize: "0.8rem",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
}));

export const ChatMsgBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMine",
})(({ isMine }) => ({
  maxWidth: "60%",
  display: "flex",
  flexDirection: "column",
  alignItems: isMine ? "flex-end" : "flex-start",
}));

export const ChatMsgCol = ChatMsgBox;

/** Chat message card - white bg, app color accent bar */
export const ChatMsgPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isMine",
})(({ isMine }) => ({
  padding: "4px 10px",
  maxWidth: "100%",
  background: "#ffffff",
  color: "#334155",
  borderRadius: isMine ? "10px 10px 4px 10px" : "10px 10px 10px 4px",
  border: "1px solid #e2e8f0",
  borderLeft: isMine ? "1px solid #e2e8f0" : `3px solid ${THEME.primary}`,
  borderRight: isMine ? `3px solid ${THEME.primary}` : "1px solid #e2e8f0",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
}));

export const ChatMsgText = styled(Typography)({
  lineHeight: 1.4,
  fontSize: "12px",
  fontWeight: 400,
});

/** Sender name label (for incoming messages) */
export const ChatMsgSender = styled(Typography)({
  fontSize: "10px",
  fontWeight: 400,
  color: THEME.primaryRgba(0.65),
  marginBottom: 2,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

/** Date/time - small size */
export const ChatMsgTime = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isMine",
})(({ isMine }) => ({
  marginTop: 2,
  fontSize: "9px",
  color: THEME.primaryRgba(0.65),
  whiteSpace: "nowrap",
  textAlign: isMine ? "right" : "left",
}));

/** Input - search/message box at bottom */
export const InputBar = styled(Box)({
  flexShrink: 0,
  backgroundColor: THEME.primaryRgba(0.03),
  borderTop: `2px solid ${THEME.primaryRgba(0.2)}`,
  padding: "8px 12px",
});

export const InputBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: 0,
  background: "transparent",
});

export const InputShell = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: "6px 12px",
  transition: "all 0.2s ease",
  "&:focus-within": {
    borderColor: THEME.primary,
    boxShadow: `0 0 0 2px ${THEME.primaryRgba(0.08)}`,
  },
});

export const InputField = styled(TextField)({
  flex: 1,
  "& .MuiInputBase-root": { padding: "2px 0", fontSize: "11px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input::placeholder": { color: "#94a3b8", opacity: 1 },
});

export const IconBtn = styled(Button)({
  minWidth: 0,
  width: 32,
  height: 32,
  borderRadius: "50%",
  padding: 0,
  color: "#64748b",
  "&:hover": { backgroundColor: "#f1f5f9" },
});

export const SendButton = styled(Button)({
  minWidth: 0,
  width: 32,
  height: 32,
  borderRadius: 12,
  backgroundColor: THEME.primary,
  color: "#fff",
  fontSize: "14px",
  "&:hover": { backgroundColor: THEME.hover },
});

export const TypingIndicator = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.5),
  flexShrink: 0,
  fontSize: "10px",
  color: "#64748b",
}));
